<template>
  <div class="relative group hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" :class="{ 'bg-gray-100 dark:bg-gray-800': editingDependencies }">
    <!-- Sticky while this section's body scrolls underneath, so it's always
         clear which section is open — stickyTop is this page's own measured
         header height, passed down from factsheet.vue. -->
    <div class="relative px-5 py-4 flex items-center gap-2 sticky z-[5]"
      :class="editingDependencies ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-[#1C2128]'" :style="{ top: `${stickyTop}px` }">
      <button type="button" title="Collapse section" @click="toggleCollapsed"
        class="w-5 h-5 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer flex-shrink-0">
        <svg class="w-3.5 h-3.5 transition-transform" :class="collapsed ? '-rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
        </svg>
      </button>
      <div class="w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
        <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
        </svg>
      </div>
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white">
        Dependencies
        <span v-if="dependencies.length" class="font-normal text-gray-400 dark:text-gray-500">({{ dependencies.length }})</span>
      </h2>

      <!-- Hover-reveal edit affordance, same pattern as Information's own Edit
           button — members (canEdit false) never see either state. -->
      <button v-if="canEdit && !editingDependencies" type="button" @click="toggleEditingDependencies"
        class="absolute top-3.5 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-800">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
        </svg>
        Edit
      </button>
      <!-- While editing, a reliable (not hover-gated) way back out, since the
           per-row Edit/Delete/Attach affordances below are only visible in this state. -->
      <button v-else-if="canEdit" type="button" title="Close editing" @click="toggleEditingDependencies"
        class="absolute top-3.5 right-5 w-7 h-7 flex items-center justify-center text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- pl-[92px] lines up with Information's own field labels (e.g.
         "Criticality") directly above: px-5 (20px) + the avatar's w-14
         (56px) + gap-4 (16px) = 92px. -->
    <div v-show="!collapsed" class="pl-[92px] pr-5">
      <div class="flex items-center justify-between mb-2 gap-2">
        <div class="flex items-center gap-4 min-w-0">
          <p v-if="componentOptionsStore.allFetchedAt && componentOptionsStore.allComponents.length === 0" class="text-sm text-gray-400 dark:text-gray-500 truncate">
            No components in this organization yet.
            <router-link :to="`/organizations/${organizationUrl}/it_components`" data-turbo-prefetch="false" class="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700">Go to IT Components</router-link>
          </p>
          <p v-else-if="dependencies.length === 0 && !(attachForm.open && !attachForm.editingId)" class="text-sm text-gray-300 dark:text-gray-600 italic truncate">
            Not dependent on anything yet.
          </p>
        </div>
        <button v-if="editingDependencies && !(attachForm.open && !attachForm.editingId) && componentOptionsStore.allComponents.length > 0"
          type="button" @click="openAttach"
          class="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors cursor-pointer flex-shrink-0">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          Attach
        </button>
      </div>

      <AttachDependencyForm v-if="attachForm.open && !attachForm.editingId"
        :organization-id="organizationId"
        :saving="attachForm.saving" :error="attachForm.error" submit-label="Attach"
        :existing-dependency-version-ids="existingDependencyVersionIds"
        class="mb-2" @cancel="cancelAttachForm" @submit="submitAttachForm" />

      <div v-if="!(componentOptionsStore.allFetchedAt && componentOptionsStore.allComponents.length === 0) &&
                 !(dependencies.length === 0 && !(attachForm.open && !attachForm.editingId))"
        class="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50/70 dark:bg-black/10 border-b border-gray-100 dark:border-gray-700">
              <th v-for="col in COLUMNS" :key="col.field" @click="col.sortable && toggleSort(col.field)"
                class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap transition-colors"
                :class="[col.align === 'right' ? 'text-right' : '', col.sortable ? 'cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-300' : '']">
                <span class="inline-flex items-center gap-1" :class="col.align === 'right' ? 'flex-row-reverse' : ''">
                  {{ col.label }}
                  <svg v-if="col.sortable" class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === col.field ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                    fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                    <path v-if="sortBy === col.field && sortOrder === 'asc'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                  </svg>
                </span>
              </th>
              <th v-if="editingDependencies" class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <template v-for="dep in visibleDependencies" :key="dep.id">
              <!-- Editing this row: the form replaces it entirely, spanning every column. -->
              <tr v-if="attachForm.open && attachForm.editingId === dep.id">
                <td :colspan="editingDependencies ? 7 : 6" class="p-2">
                  <AttachDependencyForm
                    :organization-id="organizationId"
                    :saving="attachForm.saving" :error="attachForm.error" submit-label="Save"
                    :initial-org-component-id="dep.org_component_id" :initial-org-component-version-id="dep.org_component_version_id"
                    :existing-dependency-version-ids="existingDependencyVersionIds"
                    @cancel="cancelAttachForm" @submit="submitAttachForm" />
                </td>
              </tr>
              <tr v-else class="group/dep hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors" :class="depRowStatusBg(dep)">
                <td class="px-3 py-2.5">
                  <div class="flex items-center gap-1.5 min-w-0">
                    <ItComponentLogo :name="dep.name" :logo-url="dep.logo_url" size="w-6 h-6" initial-text-size="text-[10px]" class="flex-shrink-0" />
                    <div class="min-w-0 leading-tight">
                      <span class="block text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{{ dep.alias || dep.name }}</span>
                      <span v-if="dep.alias" class="block text-[10px] text-gray-400 dark:text-gray-500 truncate">{{ dep.name }}</span>
                    </div>
                    <span class="inline-flex text-[9px] font-semibold rounded-full px-1.5 py-0.5 flex-shrink-0" :class="typeBadgeClass(dep.type)">{{ typeLabel(dep.type) }}</span>
                  </div>
                </td>
                <td class="px-3 py-2.5">
                  <span v-if="dep.context_type === 'Application'"
                    class="inline-flex text-[9px] font-semibold rounded-full px-1.5 py-0.5 flex-shrink-0 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                    Application
                  </span>
                  <span v-else class="inline-flex text-[9px] font-semibold rounded-full px-1.5 py-0.5 flex-shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    IT Component
                  </span>
                </td>
                <td class="px-3 py-2.5 text-xs whitespace-nowrap" :class="dep.dependency_version ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">{{ dep.dependency_version || 'Not specified' }}</td>
                <td class="px-3 py-2.5 text-xs whitespace-nowrap" :class="dep.alias ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">
                  {{ dep.alias || 'Not specified' }}
                </td>
                <td class="px-3 py-2.5 text-xs whitespace-nowrap" :class="dep.released_at ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">
                  {{ dep.released_at ? formatDependencyDate(dep.released_at) : 'Not specified' }}
                </td>
                <td class="px-3 py-2.5">
                  <div class="flex items-center gap-1.5">
                    <span class="text-xs whitespace-nowrap" :class="dep.eol ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">{{ dep.eol ? formatDependencyDate(dep.eol) : (dep.eol_not_announced ? 'Not announced' : 'Not specified') }}</span>
                    <span v-if="eolStatus(dep.eol) !== 'none'"
                      class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold cursor-default" :class="VERSION_EOL_BADGE_CLASSES[eolStatus(dep.eol)]"
                      :title="eolStatusMessage(dep.eol)">
                      {{ VERSION_EOL_BADGE_LABELS[eolStatus(dep.eol)] }}
                    </span>
                  </div>
                </td>
                <td v-if="editingDependencies" class="px-3 py-2.5">
                  <div class="flex items-center justify-end gap-1.5 opacity-0 group-hover/dep:opacity-100 transition-opacity">
                    <button type="button" @click="openEdit(dep)"
                      class="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-md cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-800 transition-colors">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                      </svg>
                      Edit
                    </button>
                    <button type="button" @click="requestDetach(dep)"
                      class="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-md cursor-pointer hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 transition-colors">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Long dependency lists stay collapsed to a manageable preview by
           default — only expanded on request, or automatically while editing
           so every row's Edit/Delete affordance is reachable without an
           extra click first. -->
      <div v-if="dependencies.length > DEFAULT_VISIBLE_COUNT" class="mt-2">
        <button v-if="!showAll" type="button" @click="showAll = true"
          class="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer">
          Show all {{ dependencies.length }} dependencies
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
          </svg>
        </button>
        <button v-else type="button" @click="showAll = false"
          class="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer">
          Show less
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
          </svg>
        </button>
      </div>
    </div>

    <ConfirmModal :open="pendingDetach.open" title="Remove dependency?"
      message="This factsheet will no longer show this dependency. You can attach it again later if needed."
      confirm-text="Delete" :saving="pendingDetach.saving" @cancel="cancelDetach" @confirm="confirmDetach">
      <template v-if="pendingDetach.dep" #subject>
        <ItComponentLogo :name="pendingDetach.dep.name" :logo-url="pendingDetach.dep.logo_url" size="w-6 h-6" initial-text-size="text-[10px]" class="flex-shrink-0" />
        <span class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ pendingDetach.dep.name }}</span>
      </template>
    </ConfirmModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useOrgApplicationsStore } from 'stores/orgApplications.js'
import { useOrgComponentOptionsStore } from 'stores/orgComponentOptions.js'
import { useApplicationFactsheetSections } from 'composables/useApplicationFactsheetSections.js'
import AttachDependencyForm from 'controllers/vue_components/organizations/applications/AttachDependencyForm.vue'
import ConfirmModal from 'controllers/vue_components/shared/ConfirmModal.vue'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import { typeLabel, typeBadgeClass } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'
import { eolStatus, VERSION_EOL_BADGE_CLASSES, VERSION_EOL_BADGE_LABELS } from 'controllers/vue_components/organizations/applications/versionEolStatus.js'

const props = defineProps({
  application: { type: Object, required: true },
  organizationId: { type: String, required: true },
  organizationUrl: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  stickyTop: { type: Number, default: 0 },
})

const emit = defineEmits(['updated', 'notify'])

const store = useOrgApplicationsStore()
const componentOptionsStore = useOrgComponentOptionsStore()
const sectionPrefs = useApplicationFactsheetSections()
const SECTION_KEY = 'dependencies'

const collapsed = computed(() => sectionPrefs.isCollapsed(SECTION_KEY))

function toggleCollapsed() {
  sectionPrefs.toggleSection(SECTION_KEY)
}

const editingDependencies = ref(false)

function toggleEditingDependencies() {
  editingDependencies.value = !editingDependencies.value
  if (editingDependencies.value) {
    sectionPrefs.expandSection(SECTION_KEY)
    showAll.value = true
  } else {
    cancelAttachForm()
  }
}

const dependencies = computed(() => props.application.dependencies || [])

// Column order drives both the header row and the sortable fields.
const COLUMNS = [
  { field: 'name', label: 'Component', sortable: true },
  { field: 'context_type', label: 'Type', sortable: true },
  { field: 'dependency_version', label: 'Version', sortable: true },
  { field: 'alias', label: 'Alias', sortable: true },
  { field: 'released_at', label: 'Released', sortable: true },
  { field: 'eol', label: 'End of life', sortable: true },
]

// No default active sort — the backend already orders dependencies by
// created_at desc (see OrgApplicationBlueprint#dependencies), so "recently
// added first" holds until the user actually clicks a column. Mirrors
// OrgComponentVersionsSection's own sorting convention.
const sortBy = ref(null)
const sortOrder = ref('desc')

function toggleSort(field) {
  if (sortBy.value === field) {
    sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortBy.value = field
    sortOrder.value = 'desc'
  }
}

// Dates: nulls (including eol_not_announced rows, whose eol is always
// cleared server-side) always sort last, regardless of direction. Mirrors
// OrgComponentVersionsSection's own compareDates.
function compareDates(a, b, dir) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return (new Date(a) - new Date(b)) * dir
}

// Strings: nulls (e.g. no alias set) always sort last too, same reasoning.
function compareStrings(a, b, dir) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) * dir
}

const sortedDependencies = computed(() => {
  if (!sortBy.value) return dependencies.value

  const dir = sortOrder.value === 'asc' ? 1 : -1
  return [...dependencies.value].sort((a, b) => {
    if (sortBy.value === 'released_at' || sortBy.value === 'eol') {
      return compareDates(a[sortBy.value], b[sortBy.value], dir)
    }
    return compareStrings(a[sortBy.value], b[sortBy.value], dir)
  })
})

// A long dependency list reads as noise more than signal — only the most
// recently added few are shown until the user explicitly asks for the rest.
const DEFAULT_VISIBLE_COUNT = 5
const showAll = ref(false)
const visibleDependencies = computed(() =>
  showAll.value ? sortedDependencies.value : sortedDependencies.value.slice(0, DEFAULT_VISIBLE_COUNT))

// EOL status background tint for table rows, same color convention as
// InformationSection's version rows. Just the background, no left-border
// accent here (the status badge in the End of life column already carries
// that signal for a table row). Healthy (Supported) rows and rows explicitly
// marked "not announced" both read as actively supported, so they share the
// same green tint — eol_not_announced rows have no eol date at all, so
// eolStatus() alone would classify them as 'none' (no tint), hence the
// explicit check ahead of it below.
const DEP_ROW_BG_CLASSES = {
  none: '',
  healthy: 'bg-green-50/40 dark:bg-green-900/10',
  warning: 'bg-amber-50/40 dark:bg-amber-900/10',
  expired: 'bg-red-50/50 dark:bg-red-900/10',
}

function depRowStatusBg(dep) {
  if (dep.eol_not_announced) return DEP_ROW_BG_CLASSES.healthy
  return DEP_ROW_BG_CLASSES[eolStatus(dep.eol)]
}

function eolStatusMessage(eolDate) {
  const status = eolStatus(eolDate)
  if (status === 'expired') return 'End of life and support has ended for this component. An upgrade is recommended.'
  if (status === 'warning') return 'End of life is within the next 6 months. Plan an upgrade ahead of time.'
  if (status === 'healthy') return 'Supported for more than 6 months.'
  return ''
}

// Single shared form state for both the Attach flow and every row's Edit —
// only one inline form is ever open at a time.
const attachForm = ref({ open: false, editingId: null, saving: false, error: '' })

// Flags a version suggestion as "Already added" in AttachDependencyForm's
// Version field — excludes the dependency currently being edited (if any) so
// editing a row doesn't flag its own current version as a duplicate of itself.
const existingDependencyVersionIds = computed(() =>
  dependencies.value.filter((d) => d.id !== attachForm.value.editingId).map((d) => d.org_component_version_id))

function openAttach() {
  attachForm.value = { open: true, editingId: null, saving: false, error: '' }
}

function openEdit(dependency) {
  attachForm.value = { open: true, editingId: dependency.id, saving: false, error: '' }
}

function cancelAttachForm() {
  attachForm.value = { open: false, editingId: null, saving: false, error: '' }
}

function formatDependencyDate(iso) {
  if (!iso) return 'Not specified'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function actionErrorMessage(res) {
  const validationErrors = res.error?.data?.data
  if (Array.isArray(validationErrors) && validationErrors.length) return validationErrors.join(', ')
  return 'Something went wrong. Please try again.'
}

async function submitAttachForm(payload) {
  attachForm.value.saving = true
  attachForm.value.error = ''

  const { editingId } = attachForm.value
  const res = editingId
    ? await store.updateDependency(props.application.id, editingId, payload)
    : await store.attachDependency(props.application.id, payload)

  attachForm.value.saving = false

  if (!res.js_error && res.response.success) {
    emit('updated', res.response.data)
    cancelAttachForm()
    emit('notify', `Dependency ${editingId ? 'updated' : 'attached'}.`, 'notice')
  } else {
    attachForm.value.error = actionErrorMessage(res)
  }
}

// Confirmation is required before a detach actually fires, since the
// Delete button sits right next to Edit under the same hover reveal and a
// stray click would otherwise remove a dependency with no way back other
// than re-attaching it from scratch.
const pendingDetach = ref({ open: false, dep: null, saving: false })

function requestDetach(dep) {
  pendingDetach.value = { open: true, dep, saving: false }
}

function cancelDetach() {
  pendingDetach.value = { open: false, dep: null, saving: false }
}

async function confirmDetach() {
  const { dep } = pendingDetach.value
  if (!dep) return

  pendingDetach.value.saving = true
  const res = await store.detachDependency(props.application.id, dep.id)
  pendingDetach.value.saving = false

  if (!res.js_error && res.response.success) {
    emit('updated', res.response.data)
    cancelDetach()
  } else {
    emit('notify', actionErrorMessage(res), 'alert')
    cancelDetach()
  }
}

onMounted(() => {
  componentOptionsStore.fetchAllIfNeeded(props.organizationId)
})
</script>
